import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import {
  Trophy,
  RotateCcw,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Clock,
  ArrowRight,
  Filter,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Sparkles,
  ArrowLeft,
  Check,
} from 'lucide-react';
import { useToast } from '../ui/Toast';

export const ScorecardView: React.FC = () => {
  const {
    latestAttempt,
    questions,
    questionSets,
    setCurrentView,
    setActiveQuestionSet,
  } = useApp();
  const { showToast } = useToast();

  const [filter, setFilter] = useState<'all' | 'wrong' | 'correct'>('all');
  const [expandedQuestions, setExpandedQuestions] = useState<Record<number, boolean>>({});

  if (!latestAttempt) {
    return (
      <div className="text-center py-20">
        <p className="text-[#5F6368] mb-4">No recent test results to display.</p>
        <button
          onClick={() => setCurrentView('student-dashboard')}
          className="px-5 py-2.5 bg-[#56E8E1] text-[#111111] hover:bg-[#34d5cd] rounded-xl text-xs font-bold cursor-pointer transition-all"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  const qSet = questionSets.find((qs) => qs.id === latestAttempt.question_set_id);

  const answersList = Array.isArray(latestAttempt.answers) ? latestAttempt.answers : [];

  // Filtered answers
  const filteredAnswers = answersList.filter((ans) => {
    if (filter === 'correct') return ans.is_correct;
    if (filter === 'wrong') return !ans.is_correct && ans.selected_option !== null;
    return true;
  });

  const minutes = Math.floor(latestAttempt.time_spent_seconds / 60);
  const seconds = latestAttempt.time_spent_seconds % 60;

  const handleRetake = (mode: 'revision' | 'test') => {
    if (qSet) {
      setActiveQuestionSet(qSet);
      showToast(`Retaking ${qSet.name} in ${mode} mode`, 'info');
      if (mode === 'revision') setCurrentView('quiz-revision');
      else setCurrentView('quiz-test');
    } else {
      setCurrentView('student-dashboard');
    }
  };

  const toggleExpand = (qId: number) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [qId]: !prev[qId],
    }));
  };

  const isHighScorer = latestAttempt.score_percentage >= 80;
  const isModerateScorer = latestAttempt.score_percentage >= 50;

  return (
    <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8 pb-24 md:pb-16">
      {/* 1. Scorecard Hero Banner */}
      <div className="bg-white rounded-3xl border border-[#E5E9E9] p-6 sm:p-8 shadow-[0_4px_20px_rgba(17,17,17,0.03)] text-center relative overflow-hidden">
        <div className="max-w-xl mx-auto relative z-10">
          <div className="w-14 h-14 rounded-2xl bg-[#FFF7D9] border border-[#EBC138]/40 text-[#8a6508] flex items-center justify-center mx-auto mb-4 shadow-xs">
            <Trophy className="w-7 h-7 text-[#8a6508]" />
          </div>

          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E8FAF9] text-[#1b8e88] text-xs font-bold uppercase tracking-wider mb-2 border border-[#56E8E1]/40">
            <span>{latestAttempt.mode === 'revision' ? 'Revision Completed' : 'Official Exam Scorecard'}</span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111111] mb-1">
            {latestAttempt.question_set_name || 'Practice Performance'}
          </h1>
          <p className="text-xs text-[#5F6368] mb-6">
            Subject: <strong className="text-[#111111]">{latestAttempt.subject_name || 'General'}</strong> • Completed{' '}
            {new Date(latestAttempt.completed_at).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>

          {/* Radial / Donut Score Card */}
          <div className="p-6 rounded-3xl bg-[#F7FAFA] border border-[#E5E9E9] mb-6 flex flex-col items-center justify-center">
            <div
              className={`text-5xl sm:text-6xl font-black tracking-tight mb-2 ${
                isHighScorer
                  ? 'text-[#20A66A]'
                  : isModerateScorer
                  ? 'text-[#8a6508]'
                  : 'text-[#D94A5C]'
              }`}
            >
              {latestAttempt.score_percentage}%
            </div>
            <div className="text-xs sm:text-sm font-semibold text-[#111111] max-w-sm">
              {isHighScorer
                ? 'Outstanding Mastery! You are well-prepared on these concepts.'
                : isModerateScorer
                ? 'Good performance! Review the questions you missed below to reinforce weak areas.'
                : 'Needs revision. Review explanations below and retry the test to build confidence.'}
            </div>
          </div>

          {/* Primary Action Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {latestAttempt.wrong_answers > 0 && (
              <button
                onClick={() => setFilter('wrong')}
                className="px-4 py-2.5 rounded-xl bg-[#D94A5C] hover:bg-[#c0384a] text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                <XCircle className="w-4 h-4" />
                Review Mistakes ({latestAttempt.wrong_answers})
              </button>
            )}

            <button
              onClick={() => handleRetake('revision')}
              className="px-4 py-2.5 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <BookOpen className="w-4 h-4 text-[#111111]" />
              Practice in Revision Mode
            </button>

            <button
              onClick={() => handleRetake('test')}
              className="px-4 py-2.5 rounded-xl bg-[#111111] hover:bg-[#222222] text-white text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
            >
              <RotateCcw className="w-4 h-4" />
              Retake Exam
            </button>

            <button
              onClick={() => setCurrentView('student-dashboard')}
              className="px-4 py-2.5 rounded-xl border border-[#E5E9E9] bg-white hover:bg-[#F0F4F4] text-[#111111] text-xs font-bold transition-all cursor-pointer"
            >
              Dashboard
            </button>
          </div>
        </div>

        {/* Decorative background aura */}
        <div className="absolute left-1/2 -top-20 -translate-x-1/2 w-96 h-96 rounded-full bg-[#56E8E1]/10 blur-3xl pointer-events-none" />
      </div>

      {/* 2. Metrics Breakdown 4-Card Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between text-xs text-[#5F6368] mb-1">
            <span className="font-bold uppercase tracking-wider text-[10px]">Correct</span>
            <CheckCircle2 className="w-4 h-4 text-[#20A66A]" />
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#20A66A]">
            {latestAttempt.correct_answers}
          </div>
          <div className="text-[11px] text-[#5F6368] mt-0.5">
            of {latestAttempt.total_questions} questions
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between text-xs text-[#5F6368] mb-1">
            <span className="font-bold uppercase tracking-wider text-[10px]">Incorrect</span>
            <XCircle className="w-4 h-4 text-[#D94A5C]" />
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#D94A5C]">
            {latestAttempt.wrong_answers}
          </div>
          <div className="text-[11px] text-[#5F6368] mt-0.5">
            Review suggested
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between text-xs text-[#5F6368] mb-1">
            <span className="font-bold uppercase tracking-wider text-[10px]">Skipped</span>
            <HelpCircle className="w-4 h-4 text-[#5F6368]" />
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#5F6368]">
            {latestAttempt.skipped_answers}
          </div>
          <div className="text-[11px] text-[#5F6368] mt-0.5">
            Unanswered questions
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between text-xs text-[#5F6368] mb-1">
            <span className="font-bold uppercase tracking-wider text-[10px]">Time Spent</span>
            <Clock className="w-4 h-4 text-[#1b8e88]" />
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#111111]">
            {minutes}m {seconds}s
          </div>
          <div className="text-[11px] text-[#5F6368] mt-0.5">
            Total test duration
          </div>
        </div>
      </div>

      {/* 3. Detailed Answer Review with Tab Filters */}
      <div className="bg-white rounded-3xl border border-[#E5E9E9] p-5 sm:p-6 shadow-[0_4px_20px_rgba(17,17,17,0.03)] space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#E5E9E9] pb-4">
          <div>
            <h2 className="text-lg font-bold text-[#111111] tracking-tight">
              Question-by-Question Solution Review
            </h2>
            <p className="text-xs text-[#5F6368]">
              Inspect your answers against the answer key and read detailed explanations.
            </p>
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-1 bg-[#F0F4F4] p-1 rounded-xl self-start sm:self-auto border border-[#E5E9E9]/60">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                filter === 'all'
                  ? 'bg-white text-[#111111] shadow-2xs'
                  : 'text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              All ({answersList.length})
            </button>
            <button
              onClick={() => setFilter('wrong')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                filter === 'wrong'
                  ? 'bg-white text-[#D94A5C] shadow-2xs'
                  : 'text-[#5F6368] hover:text-[#D94A5C]'
              }`}
            >
              Wrong Only ({latestAttempt.wrong_answers})
            </button>
            <button
              onClick={() => setFilter('correct')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                filter === 'correct'
                  ? 'bg-white text-[#20A66A] shadow-2xs'
                  : 'text-[#5F6368] hover:text-[#20A66A]'
              }`}
            >
              Correct Only ({latestAttempt.correct_answers})
            </button>
          </div>
        </div>

        {/* Questions Breakdown List */}
        <div className="space-y-3.5">
          {filteredAnswers.length === 0 ? (
            <div className="text-center py-12 text-[#5F6368] text-xs">
              No questions found for the selected filter.
            </div>
          ) : (
            filteredAnswers.map((ans, idx) => {
              const qObj = questions.find((q) => q.id === ans.question_id);
              if (!qObj) return null;

              const isExpanded = !!expandedQuestions[ans.question_id];
              const isSkipped = ans.selected_option === null;

              return (
                <div
                  key={ans.question_id}
                  className={`rounded-2xl border transition-all overflow-hidden ${
                    ans.is_correct
                      ? 'border-[#20A66A]/30 bg-[#EAF8F1]/40'
                      : isSkipped
                      ? 'border-[#E5E9E9] bg-[#F7FAFA]'
                      : 'border-[#D94A5C]/30 bg-[#FDEDEF]/40'
                  }`}
                >
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-6 h-6 rounded-lg text-xs font-extrabold flex items-center justify-center shrink-0 ${
                            ans.is_correct
                              ? 'bg-[#20A66A] text-white'
                              : isSkipped
                              ? 'bg-[#5F6368] text-white'
                              : 'bg-[#D94A5C] text-white'
                          }`}
                        >
                          {ans.is_correct ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                        </span>
                        <span
                          className={`text-xs font-bold uppercase tracking-wider ${
                            ans.is_correct
                              ? 'text-[#20A66A]'
                              : isSkipped
                              ? 'text-[#5F6368]'
                              : 'text-[#D94A5C]'
                          }`}
                        >
                          {ans.is_correct ? 'Correct' : isSkipped ? 'Skipped' : 'Incorrect'}
                        </span>
                      </div>

                      <span className="text-[11px] font-bold text-[#5F6368] uppercase">
                        {qObj.difficulty}
                      </span>
                    </div>

                    <h3 className="text-sm sm:text-base font-bold text-[#111111] mb-3">
                      {qObj.question_text}
                    </h3>

                    {/* Answer Comparison Strip */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mb-3">
                      <div
                        className={`p-2.5 rounded-xl border flex items-center justify-between ${
                          ans.is_correct
                            ? 'bg-[#EAF8F1] border-[#20A66A]/40 text-[#111111] font-bold'
                            : isSkipped
                            ? 'bg-[#F0F4F4] border-[#E5E9E9] text-[#5F6368]'
                            : 'bg-[#FDEDEF] border-[#D94A5C]/40 text-[#111111] font-bold'
                        }`}
                      >
                        <span>Your Answer:</span>
                        <span>
                          {ans.selected_option ? `Option ${ans.selected_option}` : 'Skipped / Left blank'}
                        </span>
                      </div>

                      <div className="p-2.5 rounded-xl bg-[#EAF8F1] border border-[#20A66A]/40 text-[#111111] font-bold flex items-center justify-between">
                        <span>Correct Answer:</span>
                        <span>Option {ans.correct_option}</span>
                      </div>
                    </div>

                    {/* Explanation Toggle */}
                    <button
                      onClick={() => toggleExpand(ans.question_id)}
                      className="text-xs font-bold text-[#1fb4ac] hover:text-[#1b8e88] flex items-center gap-1 cursor-pointer pt-1"
                    >
                      <span>{isExpanded ? 'Hide Detailed Solution' : 'View Detailed Solution'}</span>
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>

                    {isExpanded && (
                      <div className="mt-3 p-4 rounded-xl bg-white border border-[#E5E9E9] text-xs sm:text-sm text-[#111111] leading-relaxed animate-in fade-in duration-150">
                        <div className="flex items-center gap-1.5 text-[#1b8e88] text-xs font-bold uppercase tracking-wider mb-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-[#1fb4ac]" />
                          Explanation
                        </div>
                        <p className="whitespace-pre-line text-[#111111]">
                          {qObj.explanation || 'No detailed explanation provided for this question.'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ScorecardView;
